import { formatStation } from '../constants/stationConfig';
import type { DrishtiSession } from '../services/drishtiSession';
import type { DrishtiInstallConfigState } from '../slice/drishtiInstallConfigSlice';

export interface SupportMailContext {
    readonly session?: DrishtiSession;
    readonly install?: DrishtiInstallConfigState;
}

// The same inbox DynamicConfigHandler hands FleetEdge for Send Feedback to Amazon.
const SUPPORT_EMAIL = 'fleetedgesupport@amazon.com';

const SUBJECT = 'Drishti Installer App problem report';

const PROMPT = ['Describe the problem:', '', '', '--- Where you were (please leave this in) ---'];

const MAX_VALUE_LENGTH = 200;

const describeUser = (session: DrishtiSession): string | undefined => {
    const who = session.email ?? session.username;
    const company = session['custom:company'];
    if (!who) {
        return undefined;
    }
    return company ? `${who} [${company}]` : who;
};

const describeCalibration = ({ calibration }: DrishtiInstallConfigState): string | undefined => {
    if (!calibration.status) {
        return undefined;
    }
    const phase = calibration.phase ?? 'unknown phase';
    return `${phase} / ${calibration.status} (attempt ${calibration.attemptCount})`;
};

const installLines = (install: DrishtiInstallConfigState): [string, string | undefined][] => {
    const { site, stationNumber, deviceSerial, calibration } = install;
    return [
        ['Station', site && stationNumber ? formatStation({ site, stationNumber }) : undefined],
        ['Device', deviceSerial ?? undefined],
        ['Calibration', describeCalibration(install)],
        ['Failure', calibration.failure ? `${calibration.failure.code}: ${calibration.failure.message}` : undefined],
        ['Escalated', calibration.escalated ? 'yes' : undefined],
        ['Last error', calibration.lastError ?? undefined],
    ];
};

const composeBody = ({ session, install }: SupportMailContext): string => {
    const lines: [string, string | undefined][] = [
        ['Reported by', session && describeUser(session)],
        ...(install ? installLines(install) : []),
        ['Reported at', new Date().toISOString()],
    ];
    const rendered = lines
        .filter((entry): entry is [string, string] => entry[1] !== undefined)
        .map(([label, value]) => `${label}: ${value.slice(0, MAX_VALUE_LENGTH)}`);
    return [...PROMPT, ...rendered].join('\n');
};

export const buildSupportMailtoHref = (context: SupportMailContext): string => {
    const subject = encodeURIComponent(SUBJECT);
    const body = encodeURIComponent(composeBody(context));
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
};
